import React, { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { ElementLoader } from 'ui';
import { deepEquals } from 'app/infrastructure/js';
import { STATE } from 'app/constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { withOptions } from 'app/components/options-handler/with-options';
import { OptionsProps } from 'app/components/options-handler';
import { getUpdatedFilters } from './utils/get-updated-filters';
import { actionCreators } from './actions';
import { searchFiltersTranslations } from './constants';
import { SearchContainer } from './search-container';
import { SearchFiltersState } from './types-store';
import { SearchFiltersExternalProps, SerializePayload } from './types';

type DebouncedSearchFiltersExternalProps = SearchFiltersExternalProps &
{ debouncedUpdateSearchModel: (values: SerializePayload) => void };

export type SearchFiltersProps = DefaultContainerProps &
  SearchFiltersState &
  DebouncedSearchFiltersExternalProps &
  typeof actionCreators &
  OptionsProps;

class SearchFiltersComponent extends React.Component<SearchFiltersProps> {

  public static statePath: string = STATE.searchFilters;

  public componentDidMount() {
    this.updateArea();
  }

  private updateArea = () => {

    const { filtersConfig, savedFiltersConfig, queryFilters, defaultFilterValues, initialDefaultFilterValues,
      updateSavedFiltersConfig, clearQuery, clearAllFilters, setQueryValues, updateFilterValues } = this.props;

    /**
     * Clear everything when user is navigating from other area
     */

    if (!deepEquals(savedFiltersConfig, filtersConfig)) {

      clearQuery();
      setQueryValues(queryFilters);

      clearAllFilters(initialDefaultFilterValues);

      Object.keys(defaultFilterValues || {}).forEach(key => {
        updateFilterValues(key, defaultFilterValues[key]);
      });

      updateSavedFiltersConfig(filtersConfig);
    }
  }

  public componentDidUpdate(prevProps: SearchFiltersProps) {

    this.updateArea();
    this.resetDefaultFilterValues(prevProps);
  }

  private resetDefaultFilterValues = (prevProps: SearchFiltersProps) => {
    const { defaultFilterValues = {}, initialDefaultFilterValues = {}, updateFilterValuesTranslation } = this.props;
    const { defaultFilterValues: prevDefaultFilterValues = {},
      initialDefaultFilterValues: prevInitialDefaultFilterValues = {} } = prevProps;

    const updatedDefaultFilters = getUpdatedFilters(defaultFilterValues, prevDefaultFilterValues);
    const updatedInitialDefaultFilters = getUpdatedFilters(initialDefaultFilterValues, prevInitialDefaultFilterValues);

    if (Object.keys(updatedDefaultFilters).length > 0 || Object.keys(updatedInitialDefaultFilters).length > 0) {
      updateFilterValuesTranslation({ ...updatedDefaultFilters, ...updatedInitialDefaultFilters });
    }
  }

  private serialize: () => SerializePayload = () => {

    const { query, queryValues, filterValues, filterMenuValues,
      filterValidation, customFilters } = this.props;

    return {
      query,
      queryValues,
      formValues: filterValues,
      menuValues: filterMenuValues,
      filters: customFilters,
      filterValidation
    };
  }

  private updateSearchModel = () => {
    this.props.updateSearchModel(this.serialize());
  };

  private debouncedUpdateSearchModel = () => {
    this.props.debouncedUpdateSearchModel(this.serialize());
  }

  public render() {

    const {
      children, rightToolbar, topToolbar, hideDrawer, hideSearch,
      savedFiltersConfig, filtersConfig, wideScreen,
      translation, options, loadOptions,
      queryFilters, predefinedFilters, customFilters, defaultFilterValues,
      query, queryValues, updateQuery, updateQueryValue, setQueryValues,
      filterValues, filterMenuValues, filterValidation, initialDefaultFilterValues,
      updateFilterValues, updateFilterMenuValues, updateFilterValidation, clearAllFilters,
      filterOpen, openCloseFilter
    } = this.props;

    const { sharedState: { locale = '' } = {} } = this.props;

    if (!deepEquals(savedFiltersConfig, filtersConfig) ||
      (!hideDrawer && (
        (!predefinedFilters || !predefinedFilters.values?.length) &&
        (!customFilters || !customFilters.length)
      ))) {
      return <ElementLoader />;
    }

    return <SearchContainer

      translation={translation}
      locale={locale}
      hideDrawer={hideDrawer}
      hideSearch={hideSearch}
      wideScreen={wideScreen}

      updateSearchModel={this.updateSearchModel}
      debouncedUpdateSearchModel={this.debouncedUpdateSearchModel}
      topToolbar={topToolbar}
      rightToolbar={rightToolbar}

      querySettings={{
        query: query,
        values: queryValues,
        options: queryFilters,

        onChangeQuery: updateQuery,
        onChangeQueryValue: updateQueryValue,
        onSetQueryValues: setQueryValues
      }}

      filterSettings={{
        filters: customFilters,
        defaultFilterValues,

        values: filterValues,
        onChange: updateFilterValues,

        menuValues: filterMenuValues,
        onChangeMenu: updateFilterMenuValues,

        filterValidation,
        onFilterValidationChange: updateFilterValidation,

        options,
        onLoadOptions: loadOptions,

        predefinedFilters,

        openFilters: filterOpen,
        openCloseFilter,
        updateFilterValues,

        clearAllFilters: () => clearAllFilters(initialDefaultFilterValues)
      }}
    >
      {children}
    </SearchContainer>;
  }
}

const SearchFilterContainer: React.ComponentType<DebouncedSearchFiltersExternalProps> = containerFactory(
  withOptions(SearchFiltersComponent), actionCreators, undefined, searchFiltersTranslations
);

const DebouncedSearchFilters: React.FC<SearchFiltersExternalProps> = (props) => {

  const updateSearchModelModelRef = useRef(props.updateSearchModel);
  const debouncedUpdateSearchModelRef = useRef<{ (values: SerializePayload): void; cancel(): void }>();

  useEffect(() => {
    updateSearchModelModelRef.current = props.updateSearchModel;
  }, [props.updateSearchModel]);

  useEffect(() => {
    debouncedUpdateSearchModelRef.current = debounce((values: SerializePayload) => {
      updateSearchModelModelRef.current?.(values);
    }, 1000);

    return () => {
      debouncedUpdateSearchModelRef.current?.cancel();
    };
  }, []);

  return <SearchFilterContainer {...props} debouncedUpdateSearchModel={debouncedUpdateSearchModelRef.current} />;
};

export { DebouncedSearchFilters as SearchFilters };
